import React, { PropsWithChildren } from 'react'
import { toast } from '@workwhile/ui'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useUnassignRequirementFromPositionMutation } from 'queries/company'
import { usePositionDetailContextValue } from '../../../context/PositionDetailProvider'
import { usePositionDetailWorkflowsContextValue } from '../../../context/PositionDetailWorkflowProvider'

type UnassignRequirementFlowContextState = {
  state: {
    requirement: NonNullable<
      ReturnType<
        typeof usePositionDetailWorkflowsContextValue
      >['unassignRequirementFlow']['data']
    >['requirement']
    isPending: boolean
    isError: boolean
    errorMessage: string | undefined
  }
  actions: {
    confirm: () => void
  }
}

const [UnassignRequirementFlowContext, useUnassignRequirementFlowContextValue] =
  createContext<UnassignRequirementFlowContextState>({
    name: 'UnassignRequirementFlowContext',
  })

export const UnassignRequirementFlowProvider = ({
  children,
}: PropsWithChildren) => {
  const {
    state: { id: positionId },
  } = usePositionDetailContextValue()

  const {
    unassignRequirementFlow: { isOpen, close, data: flowData },
  } = usePositionDetailWorkflowsContextValue()

  const { mutate, isPending, isError, error } =
    useUnassignRequirementFromPositionMutation(positionId)

  if (isOpen && !flowData) {
    throw new Error(
      'Unable to find unassign requirement flow data. Please report this error to the engineering team.'
    )
  }

  if (!flowData) {
    return null
  }

  const value: UnassignRequirementFlowContextState = {
    state: {
      requirement: flowData.requirement,
      isPending,
      isError,
      errorMessage: parseErrorMessage(error),
    },
    actions: {
      confirm: () => {
        mutate(
          { requirementId: flowData.requirement.id },
          {
            onSuccess: () => {
              toast.success('Requirement removed successfully', {
                position: 'top-center',
              })
              close()
            },
          }
        )
      },
    },
  }

  return (
    <UnassignRequirementFlowContext.Provider value={value}>
      {children}
    </UnassignRequirementFlowContext.Provider>
  )
}

export { useUnassignRequirementFlowContextValue }
